import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Monitor, Laptop, BarChart3, FileCheck, Smartphone, ArrowRight, Zap } from 'lucide-react';
import { technologyData } from '../data/mock';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const iconMap = { Brain, Monitor, Laptop, BarChart3, FileCheck, Smartphone };

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function TechnologyPage() {
  return (
    <main className="pt-20">
      {/* Header */}
      <section className="py-16 bg-[rgb(17,17,19)] relative">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="max-w-[1200px] mx-auto px-6 relative">
          <FadeInSection>
            <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">Technology</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">Powered by Innovation</h1>
            <p className="text-[rgb(161,161,170)] text-lg max-w-2xl">{technologyData.intro}</p>
          </FadeInSection>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-[rgb(17,17,19)]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technologyData.features.map((feat, i) => {
              const Icon = iconMap[feat.icon] || Zap;
              return (
                <FadeInSection key={i} delay={i * 0.08}>
                  <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 h-full group hover:-translate-y-1 hover:border-[rgba(243,112,33,0.35)] transition-all duration-300 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#F37021] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="w-14 h-14 rounded-2xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center mb-6 group-hover:bg-[rgba(243,112,33,0.15)] transition-colors duration-300">
                      <Icon size={26} className="text-[#F37021]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{feat.title}</h3>
                    <p className="text-[rgb(161,161,170)] text-sm leading-relaxed">{feat.desc}</p>
                  </div>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Stats */}
      <section className="py-20 bg-[rgb(26,28,30)]">
        <div className="max-w-[1200px] mx-auto px-6">
          <FadeInSection>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: '99.9%', label: 'Platform Uptime' },
                { value: '10K+', label: 'Practice Questions' },
                { value: '500+', label: 'Video Lectures' },
                { value: '50+', label: 'Mock Tests' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-[#F37021] mb-2">{stat.value}</div>
                  <div className="text-[rgb(161,161,170)] text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[rgb(17,17,19)]">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <FadeInSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Experience the Future of Learning
            </h2>
            <p className="text-[rgb(161,161,170)] text-lg mb-8">
              Book a free demo session and see our technology platform in action.
            </p>
            <Link to="/contact">
              <Button className="bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl px-10 h-14 text-base border-0 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(243,112,33,0.35)]">
                Book Free Demo <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </FadeInSection>
        </div>
      </section>
    </main>
  );
}
